import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash2, X, Loader2, ShieldCheck, Pencil, Upload, Camera } from "lucide-react";

const ROLE_LABEL = { admin: "एडमिन", editor: "संपादक", reporter: "रिपोर्टर" };
const emptyForm = { name: "", email: "", password: "", designation: "", role: "reporter", photo: "", bio: "" };

export default function TeamManagement() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setMembers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm(emptyForm); setErr(""); setOpen(true); };
  const openEdit = (m) => { setEditId(m.id); setForm({ ...emptyForm, ...m, password: "" }); setErr(""); setOpen(true); };
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotoPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { setErr("केवल image फ़ाइल अपलोड करें"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Photo 5MB से छोटी हो"); return; }
    setUploadingPhoto(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      up("photo", data.url);
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "Photo अपलोड विफल");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (editId) {
        const payload = { name: form.name, designation: form.designation, role: form.role, photo: form.photo, bio: form.bio };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
      } else {
        await api.post("/users", form);
      }
      setOpen(false); load();
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "सेव नहीं हो सका");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या इस सदस्य को हटाना है?")) return;
    try { await api.delete(`/users/${id}`); load(); } catch (e) { alert(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">टीम प्रबंधन</h1>
          <p className="text-gray-500 text-sm mt-1">रिपोर्टर, संपादक जोड़ें या हटाएँ। हर सदस्य अपने नाम व पद से खबर पोस्ट करेगा।</p>
        </div>
        <button onClick={openNew} data-testid="add-member-btn" className="inline-flex items-center gap-2 bg-[#D92525] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] transition-colors">
          <UserPlus size={16} /> सदस्य जोड़ें
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {members.map((m) => (
          <div key={m.id} data-testid={`member-${m.id}`} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <img src={m.photo || "https://customer-assets-wrfwihn1.emergentagent.net/job_denva-press/artifacts/0mtpvuj1_df722c62173c41a893d7b83952155c17.png"} alt="" className="w-12 h-12 rounded-full object-cover bg-gray-100" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 truncate">{m.name}</div>
                <div className="text-xs text-gray-400 truncate">{m.email}</div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-700">{m.designation || "—"}</div>
                <span className={`inline-flex items-center gap-1 text-[11px] mt-1 px-2 py-0.5 rounded-full font-medium ${m.role === "admin" ? "bg-red-50 text-[#D92525]" : m.role === "editor" ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-600"}`}>
                  {m.role === "admin" && <ShieldCheck size={11} />} {ROLE_LABEL[m.role]}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><Pencil size={15} /></button>
                {m.id !== user?.id && (
                  <button onClick={() => remove(m.id)} data-testid={`delete-member-${m.id}`} className="p-2 rounded-lg hover:bg-red-50 text-[#D92525]"><Trash2 size={15} /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="member-form" className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">{editId ? "सदस्य संपादित करें" : "नया सदस्य"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-700"><X size={20} /></button>
            </div>
            {err && <div className="mb-4 text-sm text-[#D92525] bg-red-50 rounded px-3 py-2">{err}</div>}
            <div className="grid grid-cols-2 gap-4">
              <label className="block col-span-2"><span className="text-[11px] uppercase tracking-wider text-gray-500">नाम</span>
                <input data-testid="member-name" value={form.name} onChange={(e) => up("name", e.target.value)} required className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" /></label>
              <label className="block col-span-2"><span className="text-[11px] uppercase tracking-wider text-gray-500">ईमेल</span>
                <input data-testid="member-email" type="email" value={form.email} onChange={(e) => up("email", e.target.value)} disabled={!!editId} required className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none disabled:bg-gray-50" /></label>
              <label className="block col-span-2"><span className="text-[11px] uppercase tracking-wider text-gray-500">पासवर्ड {editId && "(बदलना हो तभी भरें)"}</span>
                <input data-testid="member-password" type="text" value={form.password} onChange={(e) => up("password", e.target.value)} required={!editId} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" /></label>
              <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-500">पद (English)</span>
                <input data-testid="member-designation" value={form.designation} onChange={(e) => up("designation", e.target.value)} placeholder="Special Correspondent" className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" /></label>
              <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-500">भूमिका</span>
                <select data-testid="member-role" value={form.role} onChange={(e) => up("role", e.target.value)} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none bg-white">
                  <option value="reporter">रिपोर्टर</option>
                  <option value="editor">संपादक</option>
                  <option value="admin">एडमिन</option>
                </select></label>
              <div className="block col-span-2">
                <span className="text-[11px] uppercase tracking-wider text-gray-500">फोटो</span>
                <div className="mt-1 flex items-center gap-4">
                  <div className="relative shrink-0">
                    {form.photo ? (
                      <img src={form.photo} alt="preview" data-testid="member-photo-preview"
                        className="w-16 h-16 rounded-full object-cover border border-gray-200 bg-gray-50" />
                    ) : (
                      <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400">
                        <Camera size={22} />
                      </div>
                    )}
                    {uploadingPhoto && (
                      <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                        <Loader2 size={18} className="text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onPhotoPick}
                      disabled={uploadingPhoto}
                      data-testid="member-photo-file"
                      className="block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#0F0F0F] file:text-white hover:file:bg-[#D92525] disabled:opacity-60"
                    />
                    {form.photo && (
                      <button
                        type="button"
                        onClick={() => up("photo", "")}
                        data-testid="member-photo-remove"
                        className="text-[11px] text-[#D92525] hover:underline"
                      >
                        हटाएँ
                      </button>
                    )}
                    <p className="text-[10px] text-gray-400 leading-tight">JPG / PNG · max 5MB · सीधे upload होगा (URL की ज़रूरत नहीं)</p>
                  </div>
                </div>
              </div>
              <label className="block col-span-2"><span className="text-[11px] uppercase tracking-wider text-gray-500">परिचय</span>
                <textarea data-testid="member-bio" value={form.bio} onChange={(e) => up("bio", e.target.value)} rows={2} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none resize-none text-sm" /></label>
            </div>
            <button data-testid="member-save" disabled={loading || uploadingPhoto} className="mt-6 w-full bg-[#D92525] text-white rounded-lg py-2.5 font-semibold hover:bg-[#0A0F1E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null} सहेजें
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
